import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { isSkuSearch, normalizeSearchTerm, hasSkuInternalBypass, SKU_INTERNAL_PARAM } from '@theme/search-sku-guard';
import { findProductsBySku } from '@theme/search-sku-lookup';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** Prevents recently-viewed:updated from re-inserting UI while Clear is in progress. */
  #clearingRecentlyViewed = false;

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      this.style.setProperty('--predictive-search-results-height', `${entry.contentRect.height}px`);
    }
  });

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      document.addEventListener(ThemeEvents.megaMenuHover, this.#blurSearch, { signal });
    }

    window.addEventListener('recently-viewed:updated', this.#handleRecentlyViewedUpdated, { signal });
    window.addEventListener('resize', this.#syncSearchDrawerCarouselOverflow, { passive: true, signal });
    this.addEventListener('click', this.#handleInDrawerSearchClick, { signal });

    this.#getRecentlyViewed();
    requestAnimationFrame(() => this.#syncSearchDrawerCarouselOverflow());

    const results = this.refs.predictiveSearchResults.firstElementChild;

    if (results) {
      this.#resizeObserver.observe(results);
    }
  }

  /**
   * Run a query inside the drawer (popular chips) without leaving the page.
   * @param {MouseEvent} event
   */
  #handleInDrawerSearchClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const trigger = target.closest?.('[data-in-drawer-search]');
    if (!(trigger instanceof HTMLElement)) return;

    const term = (trigger.dataset.inDrawerSearch || trigger.textContent || '').trim();
    if (!term) return;

    event.preventDefault();
    event.stopPropagation();
    this.#applyInDrawerSearch(term);
  };

  /**
   * Fill the input and fetch predictive results without navigating away.
   * @param {string} term
   */
  #applyInDrawerSearch(term) {
    const searchTerm = term.trim();
    if (!searchTerm) return;

    this.refs.searchInput.value = searchTerm;
    this.refs.searchInput.focus();
    this.#currentIndex = -1;
    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  expandSearch = () => {
    // Add the expanded class to the search component
    this.classList.add('predictive-search--expanded');
    if (this.dataset.activeColorScheme) {
      const target = this.dialog ?? this;
      target.classList.add(`color-${this.dataset.activeColorScheme}`);
    }

    if (!this.refs.searchInput.value) {
      this.#getRecentlyViewed();
    }

    requestAnimationFrame(() => this.#syncSearchDrawerCarouselOverflow());
  };

  #handleRecentlyViewedUpdated = () => {
    if (this.#clearingRecentlyViewed) return;
    if (this.refs.searchInput?.value) return;

    if (RecentlyViewed.getProducts().length === 0) {
      this.#removeRecentlyViewedUI();
      return;
    }

    this.#getRecentlyViewed();
  };

  /**
   * Remove the recently viewed block and restore the empty-state slot for future inserts.
   * Does not rely on Component refs — RV markup is injected after connect and refs can be stale.
   * @param {{ animate?: boolean, onDone?: () => void }} [options]
   */
  #removeRecentlyViewedUI = (options = {}) => {
    const { animate = false, onDone } = options;
    const { predictiveSearchResults } = this.refs;
    if (!(predictiveSearchResults instanceof HTMLElement)) {
      onDone?.();
      return;
    }

    const wrapper =
      this.refs.recentlyViewedWrapper instanceof HTMLElement
        ? this.refs.recentlyViewedWrapper
        : predictiveSearchResults.querySelector(
            '.recently-viewed-wrapper, #predictive-search-products.recently-viewed-wrapper'
          );

    if (!(wrapper instanceof HTMLElement)) {
      onDone?.();
      return;
    }

    const restoreSlot = () => {
      if (wrapper.isConnected) {
        const slot = document.createElement('div');
        slot.className = 'predictive-search-products-slot';
        const testId = wrapper.getAttribute('data-testid');
        if (testId) slot.setAttribute('data-testid', testId);
        wrapper.replaceWith(slot);
        this.updatedCallback();
        requestAnimationFrame(() => this.#syncSearchDrawerCarouselOverflow());
      }
      onDone?.();
    };

    if (!animate) {
      restoreSlot();
      return;
    }

    wrapper.classList.add('removing');
    onAnimationEnd(wrapper, restoreSlot);
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.search-drawer__trending-carousel, ' +
          '.predictive-search-results__wrapper, ' +
          '.search-drawer__top-links-list, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (
          container.classList.contains('predictive-search-results__wrapper-products') ||
          container.classList.contains('search-drawer__trending-carousel')
        ) {
          return Array.from(
            container.querySelectorAll(
              '.predictive-search-results__card, .search-drawer__trending-slide, .search-drawer-product-card'
            )
          );
        }

        if (container.classList.contains('search-drawer__top-links-list')) {
          return Array.from(container.querySelectorAll('.search-drawer__top-links-link'));
        }

        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter':
        if (hasSkuInternalBypass(this.refs.searchInput.value)) {
          event.preventDefault();
          this.#handleInternalSkuSearch(this.refs.searchInput.value.trim(), { mode: 'navigate' });
          return;
        }

        if (isSkuSearch(this.refs.searchInput.value)) {
          event.preventDefault();
          this.#getSearchResults(this.refs.searchInput.value.trim());
          return;
        }

        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          if (isSkuSearch(this.refs.searchInput.value)) {
            event.preventDefault();
            this.#getSearchResults(this.refs.searchInput.value.trim());
            return;
          }

          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          this.#applySearchUrlParams(searchUrl, this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.preventDefault();
    event.stopPropagation();

    this.#clearingRecentlyViewed = true;
    RecentlyViewed.clearProducts();

    this.#removeRecentlyViewedUI({
      animate: true,
      onDone: () => {
        this.#clearingRecentlyViewed = false;
      },
    });
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Block SKU searches from submitting to the search results page.
   * @param {SubmitEvent} event
   */
  onFormSubmit = async (event) => {
    const searchTerm = this.refs.searchInput.value.trim();
    if (isSkuSearch(searchTerm)) {
      event.preventDefault();
      this.#showResetButton();
      this.#getSearchResults(searchTerm);
      return;
    }

    if (hasSkuInternalBypass(searchTerm)) {
      event.preventDefault();
      await this.#handleInternalSkuSearch(searchTerm, { mode: 'navigate' });
      return;
    }

    const normalizedTerm = normalizeSearchTerm(searchTerm);
    if (normalizedTerm !== searchTerm) {
      event.preventDefault();
      const searchUrl = new URL(Theme.routes.search_url, location.origin);
      this.#applySearchUrlParams(searchUrl, searchTerm);
      window.location.href = searchUrl.toString();
    }
  };

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce(async (event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    if (hasSkuInternalBypass(searchTerm)) {
      this.#showResetButton();
      await this.#handleInternalSkuSearch(searchTerm, { mode: 'drawer' });
      return;
    }

    if (isSkuSearch(searchTerm)) {
      this.#showResetButton();
      this.#getSearchResults(searchTerm);
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * @param {URL} url
   * @param {string} searchTerm
   */
  #applySearchUrlParams(url, searchTerm) {
    const rawTerm = searchTerm.trim();
    url.searchParams.set('q', rawTerm);
    if (hasSkuInternalBypass(rawTerm)) {
      url.searchParams.set(SKU_INTERNAL_PARAM, '1');
    }
  }

  /**
   * @param {URL} url
   * @param {string} searchTerm
   */
  #applyPredictiveSearchUrlParams(url, searchTerm) {
    const rawTerm = searchTerm.trim();
    url.searchParams.set('q', rawTerm);
    if (hasSkuInternalBypass(rawTerm)) {
      url.searchParams.set(SKU_INTERNAL_PARAM, '1');
    }
  }

  /**
   * Internal "/" searches bypass Shopify search and resolve variant SKUs directly.
   * @param {string} searchTerm
   * @param {{ mode?: 'drawer' | 'navigate' }} [options]
   */
  async #handleInternalSkuSearch(searchTerm, options = {}) {
    const mode = options.mode ?? 'drawer';
    const rawTerm = searchTerm.trim();
    if (!hasSkuInternalBypass(rawTerm)) return;

    const normalizedTerm = normalizeSearchTerm(rawTerm);
    if (normalizedTerm.length < 3) {
      if (mode === 'drawer') {
        this.#showInternalSkuMessage('Type a product code after /');
      }
      return;
    }

    if (mode === 'drawer') {
      this.#showInternalSkuMessage('Searching…');
    }

    const products = await findProductsBySku(rawTerm, {
      partial: normalizedTerm.length < 5,
    });

    if (!products.length) {
      if (mode === 'drawer') {
        this.#showInternalSkuMessage(`No product found for "${normalizedTerm}".`);
      } else {
        const searchUrl = new URL(Theme.routes.search_url, location.origin);
        this.#applySearchUrlParams(searchUrl, rawTerm);
        window.location.href = searchUrl.toString();
      }
      return;
    }

    if (mode === 'navigate') {
      this.#navigateToInternalSkuSearchResults(products);
      return;
    }

    await this.#renderInternalSkuProductResults(products);
  }

  /**
   * @param {Array<Record<string, unknown>>} products
   */
  async #renderInternalSkuProductResults(products) {
    if (!this.dataset.sectionId) return;

    const query = products.map((product) => `id:${product.id}`).join(' OR ');
    const url = this.#buildInternalSkuRequestURL(query);
    const abortController = this.#createAbortController();
    const { predictiveSearchResults } = this.refs;

    try {
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (!html || abortController.signal.aborted) return;

      morph(predictiveSearchResults, html);
      this.#resetScrollPositions();
      document.dispatchEvent(
        new CustomEvent('pz-collection-cards:mount', {
          detail: { root: predictiveSearchResults },
        })
      );
      requestAnimationFrame(() => this.#syncSearchDrawerCarouselOverflow());
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * @param {Array<Record<string, unknown>>} products
   */
  #navigateToInternalSkuSearchResults(products) {
    const idQuery = products.map((product) => `id:${product.id}`).join(' OR ');
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', idQuery);
    url.searchParams.set('type', 'product');
    window.location.href = url.toString();
  }

  /**
   * @param {string} idQuery
   * @returns {URL}
   */
  #buildInternalSkuRequestURL(idQuery) {
    const url = this.#buildRecentlyViewedRequestURL(idQuery, Theme.routes.search_url);
    url.searchParams.set('internal_sku_results', '1');
    url.searchParams.set(SKU_INTERNAL_PARAM, '1');
    return url;
  }

  /**
   * @param {string} message
   */
  #showInternalSkuMessage(message) {
    const { predictiveSearchResults } = this.refs;
    if (!(predictiveSearchResults instanceof HTMLElement)) return;

    let dropdown = predictiveSearchResults.querySelector('#predictive-search-results');
    if (!(dropdown instanceof HTMLElement)) {
      dropdown = document.createElement('div');
      dropdown.id = 'predictive-search-results';
      dropdown.className = 'predictive-search-dropdown';
      dropdown.setAttribute('role', 'listbox');
      dropdown.setAttribute('aria-expanded', 'true');
      predictiveSearchResults.appendChild(dropdown);
    }

    dropdown.innerHTML = `
      <div class="predictive-search-results__inner" data-search-results></div>
    `;

    const resultsInner = dropdown.querySelector('[data-search-results]');
    if (resultsInner instanceof HTMLElement) {
      const messageEl = document.createElement('p');
      messageEl.className = 'predictive-search-results__internal-sku-message';
      messageEl.textContent = message;
      resultsInner.appendChild(messageEl);
    }

    requestAnimationFrame(() => this.#syncSearchDrawerCarouselOverflow());
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    this.#applyPredictiveSearchUrlParams(url, searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'query,product,collection,page,article');
    url.searchParams.set('resources[limit]', '10');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        document.dispatchEvent(
          new CustomEvent('pz-collection-cards:mount', {
            detail: { root: predictiveSearchResults },
          })
        );
        requestAnimationFrame(() => this.#syncSearchDrawerCarouselOverflow());
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (this.dataset.showRecentlyViewed === 'false') return null;
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const query = viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR ');
    const requestUrls = [
      this.#buildRecentlyViewedRequestURL(query, RecentlyViewed.getSearchUrl()),
      this.#buildRecentlyViewedRequestURL(query, window.location.href),
    ];

    for (const url of requestUrls) {
      try {
        const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
        if (html?.includes('id="predictive-search-products"')) return html;
      } catch {
        /* try next url */
      }
    }

    return null;
  }

  /**
   * @param {string} query
   * @param {string} baseHref
   * @returns {URL}
   */
  #buildRecentlyViewedRequestURL(query, baseHref) {
    const url = new URL(baseHref, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length > 0) {
      url.searchParams.set('rv_ids', viewedProducts.join(','));
    }
    return url;
  }

  /**
   * Fetch recently viewed products using the section renderer and update the results container.
   */
  async #getRecentlyViewed() {
    const { predictiveSearchResults } = this.refs;
    if (!(predictiveSearchResults instanceof HTMLElement)) return;
    if (this.#clearingRecentlyViewed) return;

    // Get the initial height before the results are rendered
    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!resultsMarkup) {
        if (RecentlyViewed.getProducts().length === 0) {
          this.#removeRecentlyViewedUI();
        }
        return;
      }

      const parsedNextPage = new DOMParser().parseFromString(resultsMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedNextPage.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      recentlyViewedProductsHtml.setAttribute('ref', 'recentlyViewedWrapper');

      const existingWrapper =
        this.refs.recentlyViewedWrapper instanceof HTMLElement
          ? this.refs.recentlyViewedWrapper
          : predictiveSearchResults.querySelector('.recently-viewed-wrapper');
      const slot = predictiveSearchResults.querySelector('.predictive-search-products-slot');

      if (!slot && !existingWrapper) return;

      if (abortController.signal.aborted || this.#clearingRecentlyViewed) return;

      if (existingWrapper) {
        existingWrapper.replaceWith(recentlyViewedProductsHtml);
      } else if (slot) {
        slot.replaceWith(recentlyViewedProductsHtml);
      }

      this.updatedCallback();
      requestAnimationFrame(() => this.#syncSearchDrawerCarouselOverflow());
    } catch (error) {
      throw error;
    }
  }

  /**
   * Show carousel nav only when product slides overflow the drawer width.
   * Mobile uses dots; desktop uses arrows (CSS).
   */
  #syncSearchDrawerCarouselOverflow = () => {
    const root = this.refs.predictiveSearchResults;
    if (!(root instanceof HTMLElement)) return;

    root.querySelectorAll('.search-drawer__trending-carousel').forEach((carousel) => {
      if (!(carousel instanceof HTMLElement)) return;

      const scroller = carousel.querySelector('slideshow-slides');
      const nav = carousel.querySelector('.search-drawer__trending-nav');
      if (!(scroller instanceof HTMLElement)) return;

      const overflows = scroller.scrollWidth > scroller.clientWidth + 4;
      carousel.classList.toggle('is-overflow', overflows);

      if (nav instanceof HTMLElement) {
        if (overflows) {
          nav.removeAttribute('hidden');
        } else {
          nav.setAttribute('hidden', '');
        }
      }
    });
  };

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts =
      this.dataset.showRecentlyViewed === 'false' ? [] : RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      try {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
        if (recentlyViewedMarkup) {
          const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
          const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
          if (recentlyViewedProductsHtml) {
            recentlyViewedProductsHtml.setAttribute('ref', 'recentlyViewedWrapper');

            const slot = parsedEmptySectionMarkup.querySelector('.predictive-search-products-slot');
            if (slot) {
              slot.replaceWith(recentlyViewedProductsHtml);
            }
          }
        }
      } catch {
        /* Keep empty-state trending/suggestions even if recently viewed fails to load */
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    document.dispatchEvent(
      new CustomEvent('pz-collection-cards:mount', {
        detail: { root: predictiveSearchResults },
      })
    );
    requestAnimationFrame(() => this.#syncSearchDrawerCarouselOverflow());
  };

  /**
   * Closes the predictive search.
   */
  #blurSearch = () => {
    this.refs.searchInput.blur();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
